// Platform settings. Backend: api/core/routers/system_config.py (public) and admin_system.py (platform admins).

import { ApiError, TokenGetter } from './learningApi';
import { DEFAULT_SIGNUP_CREDITS } from './config';

export type FeatureKey = 'build_guide' | 'chain_builder' | 'object_generator' | 'function_generator' | 'app_builder';
export type AnnouncementSeverity = 'info' | 'success' | 'warning' | 'error';

export interface Announcement {
  enabled: boolean;
  message: string;
  severity: AnnouncementSeverity;
  link_label: string;
  link_url: string;
}

export interface PublicConfig {
  features: Record<FeatureKey, boolean>;
  announcement: Announcement | null;
  signup_credits: number;
  signups_open: boolean;
  signups_closed_message: string | null;
}

export interface PlatformSettings {
  models: Record<string, string[] | string>;
  features: Record<FeatureKey, boolean>;
  announcement: Announcement;
  billing: {
    signup_credits: number;
    credit_buffer_percent: number;
    max_single_refill_credits: number;
  };
  limits: {
    builder_daily_calls_per_account: number;
  };
  access: {
    signups_open: boolean;
    signups_closed_message: string;
  };
  knowledge_preprocess: {
    enabled: boolean;
    models: string[];
    max_chars_per_file: number;
    max_credits_per_file: number;
    default_on_max_mb: number;
  };
}

export interface ActivityStats {
  calls: number;
  ok: number;
  failed: number;
  fallback: number;
  success_rate: number | null;
  p50_ms: number | null;
  p95_ms: number | null;
  last_at: string | null;
  last_model: string | null;
  models: { model: string; calls: number }[];
  primary: string | null;
  since_change: { calls: number; on_primary: number } | null;
}

export interface ActivityEvent {
  at: string;
  feature: string;
  model: string | null;
  error: string;
  attempts: number | null;
}

export interface BuilderActivity {
  days: number;
  since: string;
  settings_updated_at: string | null;
  settings_updated_by: string | null;
  features: (ActivityStats & { feature: string; label: string; enabled: boolean })[];
  daily: { date: string; calls: number; failed: number }[];
  recent_failures: ActivityEvent[];
  recent_fallbacks: ActivityEvent[];
  public_functions: (ActivityStats & { function: string; label: string })[];
  public_failures: ActivityEvent[];
  truncated: boolean;
}

export interface ModelOption {
  id: string;
  name: string;
  provider: string;
}

export interface SystemSettingsPayload {
  settings: PlatformSettings;
  defaults: PlatformSettings;
  updated_at: string | null;
  updated_by: string | null;
  model_options: ModelOption[];
  /** For Knowledge Hub AI organizing: `model_options` plus their variants. */
  knowledge_model_options?: ModelOption[];
  model_features: Record<string, string>;
  stack_features: string[];
  max_stack_models: number;
  feature_switches: Record<FeatureKey, string>;
  limits: {
    signup_credits_range: [number, number];
    credit_buffer_percent_max: number;
    max_refill_credits_range: [number, number];
    refill_unit: number;
    announcement_max_chars: number;
    builder_daily_cap_range: [number, number];
    signups_closed_message_max_chars: number;
    default_signups_closed_message: string;
    knowledge_max_chars_range: [number, number];
    knowledge_max_credits_range: [number, number];
    knowledge_default_on_mb_range: [number, number];
  };
}

export const DEFAULT_PUBLIC_CONFIG: PublicConfig = {
  features: { build_guide: true, chain_builder: true, object_generator: true, function_generator: true, app_builder: true },
  announcement: null,
  signup_credits: DEFAULT_SIGNUP_CREDITS,
  signups_open: true,
  signups_closed_message: null,
};

const CACHE_MS = 60_000;
let cached: { at: number; promise: Promise<PublicConfig> } | null = null;

/** Never rejects: falls back to the built-in defaults when the API is unreachable. */
export function fetchPublicConfig(force = false): Promise<PublicConfig> {
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  const promise = fetch('/api/v2/system/config')
    .then((res) => (res.ok ? res.json() : DEFAULT_PUBLIC_CONFIG))
    .then((data: Partial<PublicConfig>) => ({
      ...DEFAULT_PUBLIC_CONFIG,
      ...data,
      features: { ...DEFAULT_PUBLIC_CONFIG.features, ...(data.features || {}) },
    }))
    .catch(() => DEFAULT_PUBLIC_CONFIG);
  cached = { at: Date.now(), promise };
  return promise;
}

export function adminSystemApi(getToken: TokenGetter) {
  async function call<T>(init: RequestInit = {}, path = 'settings'): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/admin/system/${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    get: () => call<SystemSettingsPayload>(),
    save: async (settings: PlatformSettings) => {
      const out = await call<SystemSettingsPayload>({ method: 'PUT', body: JSON.stringify({ settings }) });
      fetchPublicConfig(true);
      return out;
    },
    activity: (days: number) => call<BuilderActivity>({}, `activity?days=${days}`),
  };
}
